import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from '../css-isolated/hr_tb_conclusion.module.css';
import aiEngine, { getRecommendationLabel } from '../utils/aiRecommendationEngine';


function TBConclusion({ values = {}, onChange, isDarkMode = false, formState }) {
  const defaultFurtherExam = {
    sbirt: false,
    philpen: false,
    neuro: false,
    allegation: false
  };
  const defaultFindings = {
    noInjury: false,
    noMarkings: false
  };
  const defaultImpressions = ['', '', ''];
  const recommendationOptions = [
    { key: 'dorm', label: 'to be accommodated in general dormitory' },
    { key: 'isolated', label: 'to be isolated' },
    { key: 'hospitalized', label: 'to be hospitalized' }
  ];
  // Modal and body marking states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeBodyType, setActiveBodyType] = useState(null); // 'front' or 'back'
  const [selectedMarkType, setSelectedMarkType] = useState('tattoo');
  
  // AI recommendation states
  const [aiRecommendation, setAiRecommendation] = useState(null);
  const [isAILoading, setIsAILoading] = useState(false);
  const [aiInsufficientData, setAiInsufficientData] = useState(null);
  
  const recommendation = values.recommendation || '';
  const conclusion = values.conclusion || false;
  const furtherExam = { ...defaultFurtherExam, ...(values.furtherExam || {}) };
  const findings = { ...defaultFindings, ...(values.findings || {}) };
  const impressions = values.impressions || defaultImpressions;
  const bodyMarks = values.bodyMarks || { front: [], back: [] };
  // recommendation is now a string (the selected key)

  // Get AI recommendation when component mounts or form data changes
  useEffect(() => {
    const getAIRecommendation = async () => {
      // Only run AI if we have some form data
      if (formState && Object.keys(formState).length > 2) {
        setIsAILoading(true);
        try {
          const result = await aiEngine.predict(formState);
          
          if (result.insufficientData) {
            // Not enough data for a reliable prediction
            setAiRecommendation(null);
            setAiInsufficientData(result);
          } else {
            setAiRecommendation(result);
            setAiInsufficientData(null);
          }
        } catch (error) {
          console.error('AI prediction error:', error);
        } finally {
          setIsAILoading(false);
        }
      } else {
        setAiRecommendation(null);
        setAiInsufficientData({ insufficientData: true, filledSections: 0, totalChecks: 7, score: 0 });
      }
    };

    getAIRecommendation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]); // Re-run when form data changes

  const handleFurtherExam = (key, value) => {
    onChange && onChange({ ...values, furtherExam: { ...furtherExam, [key]: value } });
  };
  const handleFindings = (key, value) => {
    onChange && onChange({ ...values, findings: { ...findings, [key]: value } });
  };
  const handleImpression = (idx, value) => {
    const updated = impressions.map((v, i) => i === idx ? value : v);
    onChange && onChange({ ...values, impressions: updated });
  };
  const handleRecommendation = (key) => {
    onChange && onChange({ ...values, recommendation: key });
  };
  const handleConclusion = (e) => {
    const isChecked = e.target.checked;
    
    // If marking as "practically healthy", clear the impressions field
    if (isChecked) {
      const clearedImpressions = impressions.map((v, i) => i === 0 ? '' : v);
      onChange && onChange({ 
        ...values, 
        conclusion: isChecked,
        impressions: clearedImpressions
      });
    } else {
      onChange && onChange({ ...values, conclusion: isChecked });
    }
  };

  // Modal handlers
  const openBodyModal = (bodyType) => {
    setActiveBodyType(bodyType);
    setIsModalOpen(true);
  };

  const closeBodyModal = () => {
    setIsModalOpen(false);
    setActiveBodyType(null);
  };

  // Body marking handlers
  const handleBodyImageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    const newMark = {
      id: Date.now(),
      type: selectedMarkType,
      x: x,
      y: y,
      description: ''
    };
    
    const updatedMarks = {
      ...bodyMarks,
      [activeBodyType]: [...bodyMarks[activeBodyType], newMark]
    };
    
    onChange && onChange({ ...values, bodyMarks: updatedMarks });
  };

  const handleRemoveMark = (markId) => {
    const updatedMarks = {
      ...bodyMarks,
      [activeBodyType]: bodyMarks[activeBodyType].filter(mark => mark.id !== markId)
    };
    onChange && onChange({ ...values, bodyMarks: updatedMarks });
  };

  const handleMarkDescriptionChange = (markId, description) => {
    const updatedMarks = {
      ...bodyMarks,
      [activeBodyType]: bodyMarks[activeBodyType].map(mark => 
        mark.id === markId ? { ...mark, description } : mark
      )
    };
    onChange && onChange({ ...values, bodyMarks: updatedMarks });
  };

  const getMarkColor = (type) => {
    const colors = {
      tattoo: '#FF0000',
      piercing: '#0000FF',
      scar: '#800080',
      burn: '#FFA500',
      bruise: '#4B0082',
      wound: '#8B0000'
    };
    return colors[type] || '#FF0000';
  };

  const markTypes = [
    { key: 'tattoo', label: 'Tattoo', color: '#FF0000' },
    { key: 'piercing', label: 'Piercing', color: '#0000FF' },
    { key: 'scar', label: 'Scar', color: '#800Purple' },
    { key: 'burn', label: 'Burn Mark', color: '#FFA500' },
    { key: 'bruise', label: 'Bruise', color: '#4B0082' },
    { key: 'wound', label: 'Wound', color: '#8B0000' }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Let wizard handle submit
  };

  return (
    <div className={`${styles['hr-wizard-container']} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`}>
      <div className={styles.tbConclusionContainer}>
        <div className={styles.contentWrapper}>
          <form onSubmit={handleSubmit} autoComplete="off">
            {/* Main Layout - Two Column Layout */}
            <div className={styles.mainLayout}>
              
              {/* Left Column - Body Diagrams */}
              <div className={styles.leftColumn}>
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className={styles.sectionTitle}>Physical Assessment</h3>
                  </div>
                  <div className={styles.tbConclusionFigure}>
                    <div className={styles.tbFigureImgs}>
                      <div className={`${styles.tbImageContainer} ${styles.frontBody}`}>
                        <div className={styles.clickableImageWrapper} onClick={() => openBodyModal('front')}>
                          <img src="/front_body.png" alt="Body front" className={styles.tbFigureImg} />
                          <div className={styles.imageOverlay}>
                            <div className={styles.overlayText}>Click to Edit</div>
                          </div>
                          {bodyMarks.front.map((mark, index) => {
                            // Count how many marks of the same type appear before this one
                            const markTypeCount = bodyMarks.front
                              .slice(0, index)
                              .filter(m => m.type === mark.type).length + 1;
                            
                            return (
                            <div
                              key={mark.id}
                              className={styles.markIndicator}
                              style={{
                                left: `${mark.x}%`,
                                top: `${mark.y}%`,
                                backgroundColor: getMarkColor(mark.type)
                              }}
                            >
                              <span style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                pointerEvents: 'none'
                              }}>
                                {markTypeCount}
                              </span>
                            </div>
                            );
                          })}
                        </div>
                        <div className={styles.tbImageLabel}>Front ({bodyMarks.front.length})</div>
                      </div>
                      <div className={`${styles.tbImageContainer} ${styles.backBody}`}>
                        <div className={styles.clickableImageWrapper} onClick={() => openBodyModal('back')}>
                          <img src="/back_body.png" alt="Body back" className={styles.tbFigureImg} />
                          <div className={styles.imageOverlay}>
                            <div className={styles.overlayText}>Click to Edit</div>
                          </div>
                          {bodyMarks.back.map((mark, index) => {
                            // Count how many marks of the same type appear before this one
                            const markTypeCount = bodyMarks.back
                              .slice(0, index)
                              .filter(m => m.type === mark.type).length + 1;
                            
                            return (
                            <div
                              key={mark.id}
                              className={styles.markIndicator}
                              style={{
                                left: `${mark.x}%`,
                                top: `${mark.y}%`,
                                backgroundColor: getMarkColor(mark.type)
                              }}
                            >
                              <span style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '10px',
                                pointerEvents: 'none'
                              }}>
                                {markTypeCount}
                              </span>
                            </div>
                            );
                          })}
                        </div>
                        <div className={styles.tbImageLabel}>Back ({bodyMarks.back.length})</div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.tbFigureFindings}>
                    <label className={styles.checkboxOption}>
                      <input 
                        type="checkbox" 
                        checked={findings.noInjury} 
                        onChange={e => handleFindings('noInjury', e.target.checked)} 
                        className={styles.checkboxInput} 
                      /> No signs of external physical injury
                    </label>
                    <label className={styles.checkboxOption}>
                      <input 
                        type="checkbox" 
                        checked={findings.noMarkings} 
                        onChange={e => handleFindings('noMarkings', e.target.checked)} 
                        className={styles.checkboxInput} 
                      /> No skin markings or unusual piercings
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column - Form Fields */}
              <div className={styles.rightColumn}>

                {/* Top Row: Health Assessment + Further Examination side by side */}
                <div className={styles.topRowSections}>

                  {/* Health Assessment Section */}
                  <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.sectionIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="currentColor" strokeWidth="2"/>
                          <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <h3 className={styles.sectionTitle}>Health Assessment</h3>
                    </div>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={styles.checkboxOption}>
                          <input 
                            type="checkbox" 
                            checked={conclusion} 
                            onChange={handleConclusion} 
                            className={styles.checkboxInput} 
                          /> Practically healthy
                        </label>
                      </div>
                      <div className={styles.formGroup}>
                        <label className={`${styles.formLabel} ${styles.optionalLabel}`}>
                          Other impressions if not practically healthy:
                          {conclusion && (
                            <span style={{fontSize: '12px', color: '#7091E6', marginLeft: '8px'}}>
                              (Disabled - Marked as practically healthy)
                            </span>
                          )}
                        </label>
                        <input 
                          type="text" 
                          className={styles.formInput} 
                          value={impressions[0]} 
                          onChange={e => handleImpression(0, e.target.value)} 
                          placeholder="Enter other impressions..." 
                          disabled={conclusion}
                          style={conclusion ? {backgroundColor: '#f0f4f8', cursor: 'not-allowed'} : {}}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Further Examination Section */}
                  <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.sectionIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </div>
                      <h3 className={styles.sectionTitle}>Further Examination</h3>
                    </div>
                    <div className={styles.formGrid}>
                      <div className={styles.formGroup}>
                        <label className={`${styles.formLabel} ${styles.optionalLabel}`}>For further examination/evaluation:</label>
                        <div className={styles.checkboxGroup}>
                          <label className={styles.checkboxOption}>
                            <input 
                              type="checkbox" 
                              checked={furtherExam.sbirt} 
                              onChange={e => handleFurtherExam('sbirt', e.target.checked)} 
                              className={styles.checkboxInput} 
                            /> SBIRT
                          </label>
                          <label className={styles.checkboxOption}>
                            <input 
                              type="checkbox" 
                              checked={furtherExam.philpen} 
                              onChange={e => handleFurtherExam('philpen', e.target.checked)} 
                              className={styles.checkboxInput} 
                            /> PhilPEN
                          </label>
                          <label className={styles.checkboxOption}>
                            <input 
                              type="checkbox" 
                              checked={furtherExam.neuro} 
                              onChange={e => handleFurtherExam('neuro', e.target.checked)} 
                              className={styles.checkboxInput} 
                            /> Neuro-Psychiatric Evaluation
                          </label>
                          <label className={styles.checkboxOption}>
                            <input 
                              type="checkbox" 
                              checked={furtherExam.allegation} 
                              onChange={e => handleFurtherExam('allegation', e.target.checked)} 
                              className={styles.checkboxInput} 
                            /> Further documentation of Allegation of Ill-Treatment
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>{/* end topRowSections */}

                {/* Recommendation Section (standalone below) */}
                <div className={styles.formSection}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h3 className={styles.sectionTitle}>Recommendation</h3>
                  </div>
                  <div className={styles.recommendationColumns}>
                    {/* Left: AI panel */}
                    <div className={styles.recommendationAiCol}>
                      {isAILoading && (
                        <div className={styles.aiLoadingContainer}>
                          <div className={styles.aiLoadingSpinner}></div>
                          <span>AI analyzing health data...</span>
                        </div>
                      )}
                      {aiRecommendation && !isAILoading && (
                        <div className={styles.aiRecommendationPanel}>
                          <div className={styles.aiHeader}>
                            <span className={styles.aiIcon}>🤖</span>
                            <h4>AI-Powered Recommendation</h4>
                            <span className={`${styles.confidenceBadge} ${styles[`confidence${aiRecommendation.confidence}`]}`}>
                              {aiRecommendation.confidence} Confidence ({aiRecommendation.confidenceScore}%)
                            </span>
                          </div>
                          <div className={styles.aiMainRecommendation}>
                            <strong>Recommended:</strong> {getRecommendationLabel(aiRecommendation.recommendation)}
                          </div>
                          <details className={styles.aiDetailsSection}>
                            <summary>View AI Analysis ({aiRecommendation.reasoning.length} factors)</summary>
                            <div className={styles.aiReasoningList}>
                              {aiRecommendation.reasoning.map((reason, idx) => (
                                <div key={idx} className={styles.aiReasonItem}>{reason}</div>
                              ))}
                            </div>
                            <div className={styles.aiProbabilities}>
                              <h5>Confidence Distribution:</h5>
                              {aiRecommendation.allProbabilities.map((rec, idx) => (
                                <div key={idx} className={styles.probabilityBar}>
                                  <span className={styles.probabilityLabel}>{rec.label}</span>
                                  <div className={styles.probabilityTrack}>
                                    <div 
                                      className={styles.probabilityFill}
                                      style={{ width: `${rec.probability * 100}%` }}
                                    ></div>
                                  </div>
                                  <span className={styles.probabilityValue}>
                                    {(rec.probability * 100).toFixed(1)}%
                                  </span>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}
                      {!aiRecommendation && !isAILoading && (
                        <div className={styles.aiInsufficientPanel}>
                          <div className={styles.aiInsufficientIcon}>🤖</div>
                          <h4 className={styles.aiInsufficientTitle}>Insufficient Data</h4>
                          <p className={styles.aiInsufficientMessage}>
                            The AI needs more health data before it can make a reliable recommendation. 
                            Please complete more sections of the health record form.
                          </p>
                          {aiInsufficientData && (
                            <div className={styles.aiProgressBar}>
                              <div className={styles.aiProgressLabel}>
                                Data completeness: {aiInsufficientData.filledSections} of {aiInsufficientData.totalChecks} sections
                              </div>
                              <div className={styles.aiProgressTrack}>
                                <div 
                                  className={styles.aiProgressFill}
                                  style={{ width: `${(aiInsufficientData.score * 100)}%` }}
                                ></div>
                              </div>
                              <div className={styles.aiProgressHint}>
                                Fill at least {Math.ceil(aiInsufficientData.totalChecks * 0.4)} sections to enable AI recommendation
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Right: Radio buttons */}
                    <div className={styles.recommendationRadioCol}>
                      <label className={styles.formLabel}>Select recommendation:</label>
                      <div className={styles.radioGroup}>
                        {recommendationOptions.map(opt => (
                          <label 
                            key={opt.key} 
                            className={`${styles.radioOption} ${aiRecommendation?.recommendation === opt.key ? styles.aiRecommended : ''}`}
                          >
                            <input
                              type="radio"
                              name="recommendation"
                              value={opt.key}
                              checked={recommendation === opt.key}
                              onChange={() => handleRecommendation(opt.key)}
                              className={styles.radioInput}
                            /> {opt.label}
                            {aiRecommendation?.recommendation === opt.key && (
                              <span className={styles.recommendedBadge}>Recommended</span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>{/* end Recommendation formSection */}
                
              </div>
            </div>

          </form>

          {/* Body Marking Modal - Rendered at document body level */}
          {isModalOpen && activeBodyType && ReactDOM.createPortal(
            <div className={`${styles.modalOverlay} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`} onClick={closeBodyModal}>
              <div className={`${styles.modalContent} ${isDarkMode ? styles['dark-mode'] : styles['light-mode']}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                  <h3 className={styles.modalTitle}>
                    Edit {activeBodyType === 'front' ? 'Front' : 'Back'} Body Markings
                  </h3>
                  <button 
                    className={styles.closeButton} 
                    onClick={closeBodyModal}
                    type="button"
                  >
                    &times;
                  </button>
                </div>
                
                <div className={styles.modalBody}>
                  {/* Left Column - Tools and Body Image */}
                  <div className={styles.modalLeftColumn}>
                    <div className={styles.markingTools}>
                      <label className={styles.formLabel}>Mark Type:</label>
                      <select 
                        value={selectedMarkType} 
                        onChange={(e) => setSelectedMarkType(e.target.value)}
                        className={styles.markTypeSelect}
                      >
                        {markTypes.map(type => (
                          <option key={type.key} value={type.key}>{type.label}</option>
                        ))}
                      </select>
                      <p className={styles.instruction}>Click on the body image to add marks</p>
                    </div>
                    
                    <div className={styles.bodyImageContainer}>
                      <div className={styles.editableImageWrapper}>
                        <img 
                          src={activeBodyType === 'front' ? '/front_body.png' : '/back_body.png'} 
                          alt={`Body ${activeBodyType}`} 
                          className={styles.editableBodyImage}
                          onClick={handleBodyImageClick}
                        />
                        {/* Render marks */}
                        {bodyMarks[activeBodyType].map((mark, index) => {
                          // Count how many marks of the same type appear before this one
                          const markTypeCount = bodyMarks[activeBodyType]
                            .slice(0, index)
                            .filter(m => m.type === mark.type).length + 1;
                          
                          return (
                          <div
                            key={mark.id}
                            className={styles.bodyMark}
                            style={{
                              left: `${mark.x}%`,
                              top: `${mark.y}%`,
                              backgroundColor: getMarkColor(mark.type)
                            }}
                            title={`${mark.type} ${markTypeCount}${mark.description ? ': ' + mark.description : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveMark(mark.id);
                            }}
                          >
                            <span style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '12px',
                              pointerEvents: 'none'
                            }}>
                              {markTypeCount}
                            </span>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column - Current Marks List */}
                  <div className={styles.modalRightColumn}>
                    {bodyMarks[activeBodyType].length > 0 ? (
                      <div className={styles.marksList}>
                        <h4 className={styles.marksListTitle}>Current Marks ({bodyMarks[activeBodyType].length})</h4>
                        <div className={styles.marksListContent}>
                          {bodyMarks[activeBodyType].map((mark, index) => {
                            // Count how many marks of the same type appear before this one
                            const markTypeCount = bodyMarks[activeBodyType]
                              .slice(0, index)
                              .filter(m => m.type === mark.type).length + 1;
                            
                            return (
                            <div key={mark.id} className={styles.markItem}>
                              <div className={styles.markInfo}>
                                <span 
                                  className={styles.markColorDot} 
                                  style={{ backgroundColor: getMarkColor(mark.type) }}
                                />
                                <span className={styles.markType}>
                                  {markTypes.find(t => t.key === mark.type)?.label} {markTypeCount}
                                </span>
                                <span className={styles.markPosition}>
                                  ({Math.round(mark.x)}%, {Math.round(mark.y)}%)
                                </span>
                                <button
                                  type="button"
                                  className={styles.removeMarkButton}
                                  onClick={() => handleRemoveMark(mark.id)}
                                  title="Remove this mark"
                                >
                                  &times;
                                </button>
                              </div>
                              <input
                                type="text"
                                placeholder="Add description..."
                                value={mark.description}
                                onChange={(e) => handleMarkDescriptionChange(mark.id, e.target.value)}
                                className={styles.markDescriptionInput}
                              />
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.marksList}>
                        <h4 className={styles.marksListTitle}>Current Marks (0)</h4>
                        <p className={styles.instruction}>No marks added yet. Click on the body image to add marks.</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.modalFooter}>
                  <button 
                    className={styles.doneButton} 
                    onClick={closeBodyModal}
                    type="button"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      </div>
    </div>
  );
}

export default TBConclusion;